<template>
  <el-dialog
    :model-value="isOpen"
    width="744px"
    @opened="opened"
    @closed="closed"
    @close="$emit('close')"
  >
    <h1 class="tw-mb-8 tw-tracking-wider">
      {{ $t('adm.permits.types.types_dialog.title_edit') }}
    </h1>
    <div v-loading="!isDataLoaded">
      <el-form
        v-if="isDataLoaded"
        ref="Form"
        :model="form"
        :rules="formRules"
        label-position="top"
        :disabled="isLoading"
        class="tw-flex tw-flex-col tw-gap-2 tw-mt-3"
        @submit.native.prevent="submitForm"
      >
        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :label="$t('common.fields.name')"
            :error="errors?.name?.[0]"
            :class="form.name ? 'has-value' : ''"
            prop="name"
          >
            <el-input
              v-model="form.name"
            />
          </i-form-item>
        </div>

        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :error="errors?.description?.[0]"
            :class="form.description ? 'has-value' : ''"
            prop="description"
            class="tw-flex-3"
          >
            <el-input
              v-model="form.description"
              type="textarea"
              :rows="5"
              :placeholder="$t('common.fields.description')"
              :maxlength="512"
            />
          </i-form-item>
        </div>
        <div class="tw-flex tw-items-center tw-gap-2">
          <i-form-item
            :label="$t('foreigners.filters.renewal_date')"
            :error="errors?.renewal_offset?.[0]"
            :class="form.renewal_offset ? 'has-value' : ''"
            prop="renewal_date"
            class="tw-flex-3"
          >
            <el-input
              v-model="form.renewal_offset"
              type="number"
            />
          </i-form-item>
          <el-form-item class="tw-flex-2">
            <el-switch
              v-model="form.case_assessment"
              :active-text="$t('foreigners.filters.case_assessment')"
            />
          </el-form-item>
        </div>
        <div class="tw-flex tw-flex-col tw-items-start tw-gap-2">
          <h4>{{ $t('adm.permits.types.types_dialog.documents') }}</h4>
          <i-form-item
            prop="documents"
          >
            <MultiSelectWithSearch
              v-model="form.documents"
              option-object-label-key="name"
              :options="documentsOptions"
              :label="$t('adm.permits.types.types_dialog.documents')"
              :handle-change="(payload) => form.documents = payload"
            />
          </i-form-item>
        </div>
        <div class="tw-flex tw-flex-col tw-items-start tw-gap-2">
          <h4>{{ $t('adm.permits.types.types_dialog.statuses') }}</h4>
          <i-form-item
            prop="statuses"
          >
            <MultiSelectWithSearch
              v-model="form.statuses"
              option-object-label-key="name"
              :options="statusesOptions"
              :label="$t('adm.permits.types.types_dialog.statuses')"
              :handle-change="(payload) => form.statuses = payload"
            />
          </i-form-item>
        </div>
        <div class="tw-flex tw-flex-col tw-items-start tw-gap-2 tw-min-h-10 tw-border tw-rounded-xl tw-border-gray tw-p-4">
          <span class="tw-text-gray-dark">
            {{ $t('adm.permits.types.types_dialog.statuses_order') }}
          </span>
          <draggable
            v-if="form.statuses.length > 0"
            :list="addedStatuses"
            item-key="name"
            class="tw-flex tw-flex-col tw-w-full tw-gap-2"
            @change="handleDragEnd"
          >
            <template
              #item="{element}"
            >
              <div class="tw-flex tw-w-full tw-items-center tw-justify-between tw-px-2 tw-py-1">
                <div>
                  {{ element.name }}
                </div>
                <icon-reorder />
              </div>
            </template>
          </draggable>
        </div>
      </el-form>
    </div>
    <template #footer>
      <div class="tw-flex tw-justify-end tw-gap-2 flex-col md:flex-row">
        <i-button
          class="tw-w-40"
          @click="$emit('close')"
        >
          {{ $t('common.actions.cancel') }}
        </i-button>
        <i-button
          type="primary"
          class="tw-w-40"
          :is-loading="isLoading"
          @click="submitForm"
        >
          {{ $t('common.actions.edit') }}
        </i-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import { deepDiff, getValidationError } from '@/helpers';
import { mapGetters } from 'vuex';
import draggable from 'vuedraggable';
import MultiSelectWithSearch from '@/components/shared/MultiSelectWithSearch.vue';

const FORM_BASE = {
  name: '', // required, maxLength: 128
  description: '', // not required, but if filled - maxLength: 512
  renewal_offset: 1, // required, greater than 0
  case_assessment: false, // required
  statuses: [], // required
  documents: [], // required
};

export default {
  name: 'EditPermitTypeDialog',
  components: { MultiSelectWithSearch, draggable },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    statusId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,
      isDataLoaded: false,
      permitType: {},
      errors: {},
      form: _cloneDeep(FORM_BASE),
      initialForm: {},
      formRules: {
        name: [
          { required: true, message: 'Required', trigger: 'blur' },
          { max: 128, message: 'Max 128 characters', trigger: 'blur' },
        ],
        description: [
          { max: 512, message: 'Max 512 characters', trigger: 'blur' },
        ],
        renewal_offset: [
          { required: true, message: 'Required', trigger: 'blur' },
          {
            trigger: 'blur',
            validator: (rule, value, callback) => {
              if (!isNaN(parseInt(value))) {
                callback();
              } else if(value < 1) {
                callback(new Error('Must be greater than zero'));
              } else {
                callback(new Error('Must be a number'));
              }
            },
          },
        ],
        case_assessment: [
          { required: true, message: 'Required', trigger: 'change' },
        ],
        statuses: [
          { required: true, message: 'Required', trigger: 'change', type: 'array', min: 1 },
        ],
        documents: [
          { required: true, message: 'Required', trigger: 'change', type: 'array', min: 1 },
        ],
      },
    };
  },

  computed: {
    ...mapGetters({
      documentsOptions: 'v2/admin/cms/permitManagement/documents/selectOptions',
      statusesOptions: 'v2/admin/cms/permitManagement/statuses/selectOptions',
    }),
    addedStatuses() {
      return this.form.statuses.map((id) => {
        return this.statusesOptions.find((item) => item.id === id);
      });
    },
  },

  watch: {
    permitType() {
      this.setInitialFormData();
    },
    isOpen(val) {
      if (val) {
        this.loadPlaceDetails();
      }
    },
  },

  methods: {
    opened() {
      this.$refs.Form?.clearValidate();
    },
    closed() {
      this.$refs.Form?.clearValidate();
    },
    handleDragEnd(changeEvent) {
      if (!changeEvent.moved) {
        return;
      }

      const { newIndex, oldIndex } = changeEvent.moved;
      const movedItem = this.form.statuses.splice(oldIndex, 1)[0];
      this.form.statuses.splice(newIndex, 0, movedItem);
    },
    async loadPlaceDetails() {
      this.isDataLoaded = false;
      this.permitType = await this.$store.dispatch('v2/admin/cms/permitManagement/permitTypes/getTypeDetails', {
        id: this.statusId,
      });
      this.isDataLoaded = true;
    },

    setInitialFormData() {
      const permitTypeKeys = Object.keys(this.permitType);
      const formKeys = Object.keys(this.form);

      formKeys.forEach((key) => {
        if (permitTypeKeys.includes(key)) {
          this.form[key] = this.permitType[key];
          this.initialForm[key] = this.permitType[key];
        }
      });

      this.$refs.Form?.clearValidate();
    },

    async submitForm() {
      this.errors = {};
      const isValid = await this.$refs.Form.validate().catch(() => false);
      if (!isValid) {
        return;
      }

      this.isLoading = true;

      try {
        const response = await this.$store.dispatch('v2/admin/cms/permitManagement/permitTypes/editType', {
          id: this.permitType.id,
          type: deepDiff(this.initialForm, this.form),
        });

        const message = _get(response, 'data.message');
        this.$message.success({
          message: this.$capitalize(message || 'Success'),
          showClose: true,
        });

        this.$emit('refresh');
        this.$emit('close');
      } catch (error) {
        this.errors = error.response.data.data;

        this.$message.error({
          message: this.$capitalize(error.response.data.message),
          showClose: true,
        });
      }

      this.isLoading = false;
    },
  },
};
</script>
